// The one rule for whether Claude is busy in a chat, in both modes. Every
// place that asks "is Claude working?" (the Working line, the composer's Stop,
// whether Rewind is offered) reads it from here, so the answers cannot drift
// apart again (ONE-6, ONE-8, ONE-12 were each a different copy of this rule).
import type { ChatItem } from '@one-deployment/protocol'
import type { Timeline } from '@one-deployment/claude-stream'
import { isLimitText } from './LimitNotice'

/**
 * - `working`: Claude is on a turn.
 * - `waiting`: the turn is paused on the person (a permission or a question).
 * - `limited`: the turn ended on a usage limit.
 * - `idle`: nothing is running. Background shells or agents may still be,
 *   and show in the live status, but Claude itself waits for you.
 */
export type ChatPhase = 'idle' | 'working' | 'waiting' | 'limited'

export type PhaseInput = {
  /** Stream mode's timeline; null in terminal mode. */
  tl: Timeline | null
  /** Terminal mode's transcript. */
  items: ChatItem[]
  /** Messages sent in terminal mode that the transcript has not shown yet. */
  sent: number
  /** Questions from the box still open (terminal mode's question cards). */
  questions: number
  /** Where a Stop was pressed (blocks or items seen then), or null. */
  stoppedAt: number | null
}

export function chatPhase({ tl, items, sent, questions, stoppedAt }: PhaseInput): ChatPhase {
  if (tl) {
    if (tl.activity === 'exited') return 'idle'
    if (tl.pending().length) return 'waiting'
    if (tl.activity === 'idle') {
      const last = tl.blocks.at(-1)
      return last?.kind === 'result' && !last.ok && isLimitText(last.text || last.errors.join(' ')) ? 'limited' : 'idle'
    }
    // A Stop shows at once: Working hides from the press until Claude writes something new.
    if (stoppedAt !== null && tl.blocks.length <= stoppedAt + 1) return 'idle'
    return 'working'
  }
  if (questions > 0) return 'waiting'
  const last = [...items].reverse().find((i) => i.kind !== 'thinking')
  if (!sent && last?.kind === 'assistant' && isLimitText(last.text ?? '')) return 'limited'
  if (!sent && !isWorking(items)) return 'idle'
  if (stoppedAt !== null && items.length <= stoppedAt + 1 && !items.slice(stoppedAt).some((i) => i.kind !== 'user' && i.kind !== 'thinking')) return 'idle'
  return 'working'
}

/** Busy for the composer: Stop shows, and a message steers the turn. */
export const isBusy = (p: ChatPhase) => p === 'working' || p === 'waiting'

/** Terminal mode: mid-turn when the newest thing is not Claude's own text (a message it has not answered, or a tool still running). */
export function isWorking(items: ChatItem[]): boolean {
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i]!
    if (it.kind === 'thinking') continue
    // Stopped: Claude records the interrupt as a user line, and waits.
    if (it.kind === 'user' && /^\[Request interrupted/.test(it.text ?? '')) return false
    if (it.kind === 'tool_result' && /^\[Request interrupted/.test(it.text ?? '')) return false
    // A refused tool ends the turn too: Claude stops and waits to be told how to go on (ONE-8).
    if (it.kind === 'tool_result' && it.is_error && /^The user doesn't want to (proceed|take this action)/.test(it.text ?? '')) return false
    return it.kind !== 'assistant'
  }
  return false
}
